// src/components/personal/sections/ClientsSection/hooks/useClientDataCore.ts
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { Client, CacheInfo } from '../types/types';
import { clientCache } from '../utils/clientCache';
import { 
  ERROR_CODES, 
  getErrorMessageStatic, 
  logError, 
  shouldRetry 
} from '../error/error-index';
import { formatSmartDate, formatPhoneNumber } from '@/utils/Formatters';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

// Объявление глобальной переменной
declare global {
  interface Window {
    __ACTIVE_FILTERS_CHECK: () => boolean;
  }
}

// Установка начального значения для проверки фильтров
window.__ACTIVE_FILTERS_CHECK = () => false; // Функция будет заменена в useClientData

// Вспомогательные функции
export function buildClientApiUrl(businessId: number | string, offset: number, limit: number): string {
  return `/api/clients/all/?business=${businessId}&offset=${offset}&limit=${limit}`;
}

// APIError для обработки ошибок API
class APIError extends Error {
  code: string;
  details?: Record<string, any>;
  
  constructor({ code, message, details }: { 
    code: string; 
    message: string; 
    details?: Record<string, any> 
  }) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.details = details;
  }
}

// Интерфейс возвращаемого значения хука
interface UseClientDataCoreReturn {
  // Состояние данных
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  allClientsLoaded: boolean;
  totalClientsCount: number;
  
  // Методы для загрузки
  loadAllClients: () => Promise<void>;
  loadClientPage: (offset: number, limit: number) => Promise<void>;
  refreshClients: (forceRefresh?: boolean, forceAllClients?: boolean) => Promise<void>;
  exportClientsToCSV: (clientsToExport: Client[]) => Promise<boolean>;
  
  // Методы работы с кешем
  clearCache: () => void;
  cacheInfo: CacheInfo | null;
}

export const useClientDataCore = (initialClients: Client[] = []): UseClientDataCoreReturn => {
  const { t } = useTranslation();
  const { businessId } = useBusinessId();
  const DEBUG = process.env.NODE_ENV === 'development';
  const { formatPrice } = useBusinessCurrency();
  
  // Состояние данных
  const [clients, setClients] = useState<Client[]>(initialClients);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allClientsLoaded, setAllClientsLoaded] = useState(false);
  const [totalClientsCount, setTotalClientsCount] = useState<number>(0);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState<boolean>(false);
  const [isActionLocked, setIsActionLocked] = useState(false);
  
  // Refs для отслеживания состояний
  const isLoadingRef = useRef(false);
  const initialLoadCompletedRef = useRef(false);
  const currentRequestIdRef = useRef<string | null>(null);
  const retryCountRef = useRef<Record<string, number>>({});
  
  // Обработка ошибок API
  const handleApiError = useCallback((error: any, context: string) => {
    // Преобразуем любую ошибку в APIError
    let apiError: APIError;
    
    if (error instanceof APIError) {
      apiError = error;
    } else if (error instanceof Error) {
      apiError = new APIError({
        code: error.name === 'TypeError' && error.message.includes('fetch') 
          ? ERROR_CODES.NETWORK_ERROR 
          : ERROR_CODES.UNKNOWN_ERROR,
        message: error.message,
        details: { stack: error.stack }
      });
    } else {
      apiError = new APIError({
        code: ERROR_CODES.UNKNOWN_ERROR,
        message: typeof error === 'string' ? error : 'Неизвестная ошибка',
        details: { originalError: error }
      });
    }
    
    // Логируем ошибку
    logError(apiError, context);
    
    // Устанавливаем сообщение об ошибке для пользователя
    const userMessage = getErrorMessageStatic(apiError, t);
    setError(userMessage);
    
    return {
      error: apiError,
      shouldRetry: shouldRetry(apiError),
      message: userMessage
    };
  }, [t]);
  
  // Генерация уникального ID запроса
  const generateRequestId = useCallback(() => {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);
  
  // Функция для повторных попыток запроса
  const retryRequest = useCallback(<T extends (...args: any[]) => Promise<any>>(
    requestFn: T,
    requestId: string,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<ReturnType<T>> => {
    return new Promise((resolve, reject) => {
      const attempt = async () => {
        try {
          const result = await requestFn();
          retryCountRef.current[requestId] = 0;
          resolve(result);
        } catch (error) {
          const { shouldRetry: shouldRetryError } = handleApiError(error, `запрос (попытка ${retryCountRef.current[requestId] || 0})`);
          
          retryCountRef.current[requestId] = (retryCountRef.current[requestId] || 0) + 1;
          
          if (shouldRetryError && retryCountRef.current[requestId] <= maxRetries) {
            if (DEBUG) console.log(`Повторная попытка ${retryCountRef.current[requestId]}/${maxRetries} через ${delay}мс`);
            setTimeout(attempt, delay);
          } else {
            delete retryCountRef.current[requestId];
            reject(error);
          }
        }
      };
      
      retryCountRef.current[requestId] = 1;
      attempt();
    });
  }, [handleApiError, DEBUG]);
  
  // Очистка кеша
  const clearCache = useCallback(() => {
    clientCache.clearCache();
    if (DEBUG) console.log('Кеш очищен');
  }, [DEBUG]);
  
  // Загрузка всех клиентов
  const loadAllClients = useCallback(async () => {
    // Базовые проверки
    if (!businessId || isLoadingRef.current) {
      if (DEBUG) console.log('loadAllClients: загрузка невозможна - уже выполняется или нет businessId');
      return;
    }
    
    // Проверка кеша
    if (clientCache.hasValidCache(businessId)) {
      const cachedClients = clientCache.getClients(businessId);
      if (cachedClients && cachedClients.length > 0) {
        if (DEBUG) console.log(`Используем ${cachedClients.length} клиентов из кэша`);
        setClients(cachedClients);
        setAllClientsLoaded(true);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        setTotalClientsCount(cachedClients.length);
        return;
      }
    }
    
    // Если все клиенты уже загружены и нет ошибок, используем их
    if (allClientsLoaded && clients.length > 0 && !error) {
      if (DEBUG) console.log('Все клиенты уже загружены, используем существующие данные');
      return;
    }
    
    // Создаем ID запроса и устанавливаем состояние загрузки
    const requestId = generateRequestId();
    currentRequestIdRef.current = requestId;
    isLoadingRef.current = true;
    setIsActionLocked(true);
    setIsLoading(true);
    setError(null);
    
    try {
      // Запрос на количество клиентов
      const fetchCountRequest = async () => {
        const response = await api.get(buildClientApiUrl(businessId, 0, 1));
        
        if (currentRequestIdRef.current !== requestId) {
          throw new Error('Запрос отменен');
        }
        
        if (!response || !response.total) {
          throw new APIError({
            code: ERROR_CODES.UNKNOWN_ERROR,
            message: 'Не удалось получить количество клиентов'
          });
        }
        
        return response.total;
      };
      
      // Получаем общее количество клиентов
      const totalCount = await retryRequest(fetchCountRequest, `${requestId}_count`);
      setTotalClientsCount(totalCount);
      
      // Настраиваем параметры пакетной загрузки
      const maxClientsToLoad = Math.min(totalCount, 10000);
      const batchSize = 500;
      const batches = Math.ceil(maxClientsToLoad / batchSize);
      
      if (DEBUG) console.log(`Загружаем всех клиентов (${totalCount}), в ${batches} пакетах`);
      
      // Временный массив для результатов
      let tempClients: Client[] = [];
      
      // Загружаем по пакетам
      for (let i = 0; i < batches; i++) {
        if (currentRequestIdRef.current !== requestId) {
          if (DEBUG) console.log('Запрос отменен во время пакетной загрузки');
          return;
        }
        
        const offset = i * batchSize;
        const limit = Math.min(batchSize, maxClientsToLoad - offset);
        
        if (DEBUG) console.log(`Загрузка пакета ${i+1}/${batches}: ${offset}-${offset+limit} из ${totalCount}`);
        
        const fetchBatchRequest = async () => {
          return await api.get(buildClientApiUrl(businessId, offset, limit));
        };
        
        const batchResponse = await retryRequest(
          fetchBatchRequest, 
          `${requestId}_batch_${i}`,
          2
        );
        
        if (batchResponse && batchResponse.success === true && Array.isArray(batchResponse.data)) {
          tempClients = [...tempClients, ...batchResponse.data];
        } else {
          console.warn('Неожиданный формат данных при загрузке клиентов:', batchResponse);
          break;
        }
      }
      
      if (currentRequestIdRef.current !== requestId) {
        if (DEBUG) console.log('Запрос отменен после пакетной загрузки');
        return;
      }
      
      if (tempClients.length > 0) {
        if (DEBUG) console.log(`Загружено ${tempClients.length} клиентов из ${totalCount}`);
        
        setClients(tempClients);
        setAllClientsLoaded(true);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        
        // Сохраняем в кеш
        clientCache.setClients(tempClients, businessId);
        if (DEBUG) console.log(`Сохранено ${tempClients.length} клиентов в кэш`);
      }
    } catch (error: any) {
      handleApiError(error, 'loadAllClients');
    } finally {
      if (currentRequestIdRef.current === requestId) {
        currentRequestIdRef.current = null;
      }
      
      setTimeout(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsActionLocked(false);
      }, 300);
    }
  }, [businessId, allClientsLoaded, clients.length, error, generateRequestId, retryRequest, handleApiError, DEBUG]);
  
  // Загрузка страницы клиентов
  // Обновленная версия loadClientPage в useClientDataCore.ts
  const loadClientPage = useCallback(async (offset: number, limit: number) => {
    // Явно проверяем флаг блокировки в начале функции
    if (window.__BLOCK_CLIENT_PAGE_LOAD) {
      if (DEBUG) console.log('loadClientPage: загрузка страницы заблокирована из-за активных фильтров/поиска');
      return;
    }
    
    if (!businessId || isLoadingRef.current) {
      if (DEBUG) console.log('loadClientPage: загрузка невозможна - уже выполняется или нет businessId');
      return;
    }
    
    // Дополнительная проверка на активные фильтры/поиск через глобальную функцию
    const hasActiveFilters = window.__ACTIVE_FILTERS_CHECK ? window.__ACTIVE_FILTERS_CHECK() : false;
    if (hasActiveFilters) {
      if (DEBUG) console.log('loadClientPage: загрузка страницы заблокирована из-за активных фильтров (проверка через __ACTIVE_FILTERS_CHECK)');
      return;
    }
    
    // Проверка кеша
    if (clientCache.hasValidCache(businessId)) {
      const cachedClients = clientCache.getClients(businessId);
      if (cachedClients && cachedClients.length > 0) {
        if (DEBUG) console.log(`Используем ${cachedClients.length} клиентов из кэша для страницы`);
        
        // Используем кеш для получения подмножества данных
        const endIndex = Math.min(offset + limit, cachedClients.length);
        const pageClients = cachedClients.slice(offset, endIndex);
        
        setClients(pageClients);
        setAllClientsLoaded(false); // Не все клиенты загружены, только страница
        setTotalClientsCount(cachedClients.length);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        
        return;
      }
    }
    
    // Создаем ID запроса и устанавливаем состояние загрузки
    const requestId = generateRequestId();
    currentRequestIdRef.current = requestId;
    isLoadingRef.current = true;
    setIsActionLocked(true);
    setIsLoading(true);
    setError(null);
    
    try {
      const fetchPageRequest = async () => {
        const url = buildClientApiUrl(businessId, offset, limit);
        if (DEBUG) console.log(`Загрузка страницы, смещение=${offset}, размер=${limit}`);
        
        return await api.get(url);
      };
      
      const response = await retryRequest(fetchPageRequest, `${requestId}_page`);
      
      if (currentRequestIdRef.current !== requestId) {
        if (DEBUG) console.log('Запрос отменен, другой запрос был начат');
        return;
      }
      
      if (!response || !response.success || !Array.isArray(response.data)) {
        throw new APIError({
          code: ERROR_CODES.UNKNOWN_ERROR,
          message: 'Неожиданный формат данных от API'
        });
      }
      
      if (DEBUG) console.log(`Получено ${response.data.length} клиентов (всего: ${response.total})`);
      
      // Дополнительная проверка перед обновлением состояния
      const currentlyHasActiveFilters = window.__ACTIVE_FILTERS_CHECK ? window.__ACTIVE_FILTERS_CHECK() : false;
      if (currentlyHasActiveFilters) {
        if (DEBUG) console.log('loadClientPage: Обнаружены активные фильтры после завершения запроса, данные не будут обновлены');
        return;
      }
      
      setClients(response.data);
      
      if (response.total) {
        setTotalClientsCount(response.total);
      }
      
      setIsInitialLoadDone(true);
      initialLoadCompletedRef.current = true;
    } catch (error: any) {
      handleApiError(error, 'loadClientPage');
    } finally {
      if (currentRequestIdRef.current === requestId) {
        currentRequestIdRef.current = null;
      }
      
      setTimeout(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsActionLocked(false);
      }, 300);
    }
  }, [businessId, generateRequestId, retryRequest, handleApiError, DEBUG]);
  
  // Обновление данных - обновленная версия функции
  const refreshClients = useCallback(async (forceRefresh: boolean = false, forceAllClients: boolean = false) => {
    if (isLoadingRef.current || isLoading) {
      if (DEBUG) console.log('Обновление уже выполняется, запрос игнорируется');
      return;
    }
    
    if (forceRefresh) {
      if (DEBUG) console.log('Принудительное обновление: очистка кеша и состояния');
      setAllClientsLoaded(false);
      setIsInitialLoadDone(false);
      initialLoadCompletedRef.current = false;
      clearCache();
    }
    
    currentRequestIdRef.current = null;
    
    // Добавлена проверка forceAllClients - если true, всегда загружаем всех клиентов
    if (forceAllClients) {
      if (DEBUG) console.log('Принудительная загрузка всех клиентов');
      await loadAllClients();
      return;
    }
    
    // По умолчанию загружаем все данные
    await loadAllClients();
  }, [loadAllClients, isLoading, clearCache, DEBUG]);
    
  // Экспорт в CSV - обновленная версия с реальной реализацией и локальным временем
  const exportClientsToCSV = useCallback(async (clientsToExport: Client[]): Promise<boolean> => {
    if (isActionLocked) {
      console.warn(t('logs.action_in_progress'));
      return false;
    }
    
    setIsActionLocked(true);
    
    try {
      if (DEBUG) console.log(`Экспорт ${clientsToExport.length} клиентов в CSV`);
      
      // Определяем заголовки CSV
      const headers = [
        'ID',
        'Имя',
        'Телефон',
        'Email',
        'Статус',
        'Количество заказов',
        'Сумма заказов',
        'Последний заказ',
        'Создан',
        'Скидка',
        'Комментарий'
      ];
      
      // Функция для перевода статуса
      const getStatusText = (status: string): string => {
        const statusMap: Record<string, string> = {
          'new': t('client_status.new', { defaultValue: 'Новый' }),
          'regular': t('client_status.regular', { defaultValue: 'Постоянный' }),
          'loyal': t('client_status.loyal', { defaultValue: 'Лояльный' }),
          'inactive': t('client_status.inactive', { defaultValue: 'Неактивный' }),
          'vip': t('client_status.vip', { defaultValue: 'VIP' })
        };
        
        return statusMap[status] || status;
      };
      
      // Экранирование специальных символов для CSV
      const escapeCSV = (value: string): string => {
        if (value === null || value === undefined) return '';
        
        const stringValue = String(value);
        
        // Если строка содержит запятые, кавычки или переносы строк, заключаем в кавычки
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          // Заменяем кавычки на двойные кавычки
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        
        return stringValue;
      };
      
      // Создаём строки с данными
      const rows = clientsToExport.map(client => [
        escapeCSV(client.id),
        escapeCSV(client.name || ''),
        escapeCSV(formatPhoneNumber(client.phone || '')),
        escapeCSV(client.email || ''),
        escapeCSV(getStatusText(client.status)),
        escapeCSV(String(client.ordersCount || 0)),
        escapeCSV(formatPrice(client.totalAmount || 0)),
        escapeCSV(client.lastOrder ? formatSmartDate(client.lastOrder) : ''),
        escapeCSV(client.created ? formatSmartDate(client.created) : ''),
        escapeCSV(`${client.discount || 0}%`),
        escapeCSV(client.comment || '')
      ]);
      
      // Добавляем заголовки в начало
      const csvContent = [headers, ...rows];
      
      // Преобразуем массив в строку CSV
      const csvString = csvContent.map(row => row.join(',')).join('\n');
      
      // Добавляем BOM для корректного отображения кириллицы в Excel
      const BOM = '\uFEFF';
      const csvStringWithBOM = BOM + csvString;
      
      // Создаем Blob с данными
      const blob = new Blob([csvStringWithBOM], {type: 'text/csv;charset=utf-8;'});
      
      // Получаем текущую локальную дату для имени файла
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const hours = String(currentDate.getHours()).padStart(2, '0');
      const minutes = String(currentDate.getMinutes()).padStart(2, '0');
      const fileName = `clients_export_${year}-${month}-${day}_${hours}-${minutes}.csv`;
      
      // Создаем URL для Blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Создаем временный элемент <a>
      const tempAnchor = document.createElement('a');
      
      // Добавляем атрибуты через прямое присваивание (обходя типизацию)
      // @ts-ignore - игнорируем проверки типов для следующих строк
      tempAnchor.href = blobUrl;
      // @ts-ignore
      tempAnchor.download = fileName;
      // @ts-ignore
      tempAnchor.style = 'display: none';
      
      // Добавляем элемент в DOM, кликаем по нему и удаляем
      // @ts-ignore
      document.body.appendChild(tempAnchor);
      // @ts-ignore
      tempAnchor.click();
      // @ts-ignore
      document.body.removeChild(tempAnchor);
      
      // Освобождаем URL ресурса
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      return true;
    } catch (error) {
      console.error('Ошибка при экспорте клиентов:', error);
      return false;
    } finally {
      setTimeout(() => {
        setIsActionLocked(false);
      }, 1000);
    }
  }, [t, isActionLocked, formatPhoneNumber, formatPrice, formatSmartDate, DEBUG]);
  
  // Базовая инициализация при монтировании
  useEffect(() => {
    if (!initialLoadCompletedRef.current && !isLoadingRef.current && businessId && !isInitialLoadDone) {
      // Проверяем состояние фильтров
      const hasActiveFilters = window.__ACTIVE_FILTERS_CHECK ? window.__ACTIVE_FILTERS_CHECK() : false;
      
      if (initialClients.length > 0) {
        if (DEBUG) console.log('Используем переданные начальные данные');
        setClients(initialClients);
        setIsInitialLoadDone(true);
        initialLoadCompletedRef.current = true;
        setTotalClientsCount(initialClients.length);
      } else if (clientCache.hasValidCache(businessId)) {
        const cachedClients = clientCache.getClients(businessId);
        if (cachedClients && cachedClients.length > 0) {
          if (DEBUG) console.log(`Используем ${cachedClients.length} клиентов из кэша для начальной загрузки`);
          setClients(cachedClients);
          setAllClientsLoaded(true);
          setIsInitialLoadDone(true);
          initialLoadCompletedRef.current = true;
          setTotalClientsCount(cachedClients.length);
          return;
        }
      } else if (hasActiveFilters) {
        // Если активны фильтры, загружаем всех клиентов вместо страницы
        if (DEBUG) console.log('Загружаем всех клиентов для фильтрации при инициализации');
        loadAllClients();
      } else {
        // Загружаем данные со смещением 0 и лимитом по умолчанию (первая страница)
        loadClientPage(0, 10);
      }
    }
  }, [businessId, initialClients, loadClientPage, loadAllClients, isInitialLoadDone, DEBUG]);
  
  // Получение информации о кеше
  const cacheInfo = useMemo(() => {
    return clientCache.getCacheInfo();
  }, [clients]); // Обновляем при изменении списка клиентов
  
  return {
    clients,
    isLoading,
    error,
    allClientsLoaded,
    totalClientsCount,
    loadAllClients,
    loadClientPage,
    refreshClients,
    exportClientsToCSV,
    clearCache,
    cacheInfo
  };
};